import React, { useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, Pager, ArticleCard, SkelGrid, SectionNote,
  SearchField, PerPageSelect, usePagedList,
  useReveal, useScrollProgress,
  excerptOf, truncate, formatDate, langPath, localized, localizedImage,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORYDETAIL — articles within one category
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (/api/categoryportail/edit/:id). This response nests its list
   one level deeper than the others — { category, articles: { data, pagination } }
   — which is exactly what usePagedList's `select` is for.

   Fixes:
   · The page title was empty until the request returned, so the header
     flashed blank and the browser tab said "undefined". The heading now shows
     a labelled placeholder while loading and never renders an empty <h1>.
   · The breadcrumb dropped the category itself; there was no way to see where
     you were beyond the title. It now reads Accueil / Actualités / {category}.
   · Filters live in the URL, real disabled pagination buttons, list dims
     instead of blanking, failure is distinct from empty — all inherited from
     the shared hook.
   · Arabic dates were formatted with the locale tag "ar-AR", which is not
     valid, so browsers silently fell back and Arabic readers were shown
     French dates. Corrected to ar-DZ in the shared formatter.
   · A missing slug produced "/article/12/undefined".
   ═══════════════════════════════════════════════════════════════════════════ */

function CategoryDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const inputRef = useRef(null);

  /* This endpoint wraps the list in `articles` and returns the category
     alongside it; `extra` carries the category through to the header. */
  const select = useCallback((data) => ({
    items: data?.articles?.data,
    pagination: data?.articles?.pagination,
    extra: data?.category ?? null,
  }), []);

  const list = usePagedList({
    url: `${BASE_URL}/api/categoryportail/edit/${id}`,
    select,
    perPageOptions: [6, 12, 24, 48],
    defaultPerPage: 12,
  });

  const pageRef = useReveal([list.items, list.state]);
  const progress = useScrollProgress();

  const clearSearch = () => {
    list.setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const linkFor = (a) => (a.slug ? `/article/${a.id}/${a.slug}` : `/article/${a.id}`);

  /* Never render an empty heading. Until the category arrives, the generic
     label stands in — the old page printed nothing and set the tab title to
     "undefined". */
  const categoryName = list.extra ? localized(list.extra, "name", lang) : "";
  const fallback = t("menu.category", t("menu.Toutacticle", "Catégorie"));
  const heading = categoryName || fallback;

  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{heading} — {t("menu.caci")}</title>
        <meta name="description" content={excerptOf(localized(list.extra, "description", lang), 30) || heading} />
        <meta property="og:title" content={heading} />
        <meta property="og:description" content={heading} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={heading}
        sub={excerptOf(localized(list.extra, "description", lang), 30)}
        crumbs={[
          { label: t("menu.home"), to: langPath(lang, "/") },
          { label: t("menu.Toutacticle", "Actualités"), to: langPath(lang, "/toutacticle") },
          { label: truncate(heading, 40) },
        ]}
        count={
          list.state === "ready" && list.total > 0
            ? { value: list.total.toLocaleString(nf), label: t("search.results", "résultats") }
            : null
        }
      />

      <div className="bar">
        <div className="caci-shell">
          <div className="bar-inner">
            <SearchField
              inputRef={inputRef}
              value={list.term}
              onChange={list.setTerm}
              onClear={clearSearch}
              placeholder={t("search.placeholder")}
              busy={list.refetching}
              clearLabel={t("home.Close")}
            />
            <div className="bar-filters">
              <PerPageSelect
                value={list.perPage}
                options={list.perPageOptions}
                onChange={(v) => list.patch({ per: v })}
                label={t("search.perPage")}
              />
            </div>
          </div>
        </div>
      </div>

      <section className="caci-section" ref={list.listRef}>
        <div className="caci-shell">
          {list.state === "loading" && <SkelGrid count={Math.min(list.perPage, 12)} />}

          {list.state === "error" && (
            <SectionNote
              state="error"
              title={t("search.errorTitle", "Chargement impossible")}
              text={t("home.section_error", "Cette catégorie n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={list.reload}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {list.state === "ready" && list.items.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                list.isFiltered ? (
                  <button type="button" className="btn-quiet" onClick={list.clearAll}>
                    <i className="bi bi-arrow-counterclockwise" />
                    {t("search.clear", "Tout effacer")}
                  </button>
                ) : null
              }
            />
          )}

          {list.state === "ready" && list.items.length > 0 && (
            <>
              <div className={`caci-grid${list.refetching ? " is-busy" : ""}`}>
                {list.items.map((a, i) => (
                  <ArticleCard
                    key={a.id ?? i}
                    index={i}
                    to={linkFor(a)}
                    image={localizedImage(a, "image", lang, BASE_URL)}
                    title={truncate(localized(a, "name", lang), 78)}
                    excerpt={excerptOf(localized(a, "description", lang), 20)}
                    date={formatDate(a.starts_at, lang, t("home.UnknownDate"))}
                    readLabel={t("home.Read_More")}
                    isRtl={isRtl}
                  />
                ))}
              </div>

              <div className="pager-wrap">
                <Pager
                  page={list.page}
                  totalPages={list.totalPages}
                  onChange={list.goToPage}
                  labels={{
                    first: t("search.first", "Première page"),
                    prev: t("search.prev", "Page précédente"),
                    next: t("search.next", "Page suivante"),
                    last: t("search.last", "Dernière page"),
                    page: t("search.page", "Page"),
                    of: t("search.of", "sur"),
                  }}
                />
              </div>
            </>
          )}

          <p className="caci-sr" aria-live="polite">
            {list.state === "ready" ? `${list.total} ${t("search.results", "résultats")}` : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

const PAGE_CSS = `
.caci-page .caci-grid.is-busy { opacity: .45; pointer-events: none; transition: opacity .2s ease; }
.caci-page .pager-wrap { margin-block-start: clamp(36px, 6vw, 64px); }
`;

export default CategoryDetail;
